import { useMemo } from 'react';
import scssVariables from '../../../../../Config.module.scss';
import LabelWithTooltip from '../../../../Components/Form/Inputs/LabelWithTooltip';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { DemeterCalculationEngineLineShortModelWithChildren, HideGroup, MarginCompositeModel } from '../../RiskDefinitions';
import StickyColumnGroup from '../../StickyColumnGroup';
import styles from './MarginInputsTable.module.scss';
import MarginInputsTableCollapsibleField from './MarginInputsTableCollapsibleField';

interface IMarginInputsTableStickyColumnProps {
    getFarmerMarginCalculatorCompositeRowsModel: MarginCompositeModel[];
    hideGroup: HideGroup;
    hideRows: string[];
    setHideGroup: (hideGroup: HideGroup) => void;
}

const MarginInputsTableStickyColumn: React.FC<IMarginInputsTableStickyColumnProps> = (props: IMarginInputsTableStickyColumnProps) => {
    const [translations] = useLanguage();

    const stickyColumns = useMemo(() => {
        const columnHeadersRow = props.getFarmerMarginCalculatorCompositeRowsModel[0];
        const levelColors = [
            scssVariables.tableRowDark,
            scssVariables.alternativeNeutralColor,
            scssVariables.alternativeNeutralColorLevel2,
            scssVariables.alternativeNeutralColorLevel3,
        ];

        const getBaseNode = (displayName: string, currentLevel: number, hideGroupKey?: keyof HideGroup, informationText?: string, isBorderless?: boolean) => ({
            sectionTitle: informationText ? <LabelWithTooltip title={displayName} tooltip={informationText} placement="top-start" /> : displayName,
            key: `${hideGroupKey ?? displayName}`,
            backgroundColor: props.hideGroup[hideGroupKey!] === false ? levelColors[currentLevel] : levelColors[currentLevel - 1],
            isBorderless,
        });

        const getNodeWithChildren = (
            displayName: string,
            currentLevel: number,
            hideGroupKey: keyof HideGroup,
            secondaryHideGroupKeys?: DemeterCalculationEngineLineShortModelWithChildren[],
        ) => {
            const showGroupChildren = props.hideGroup[hideGroupKey] === false;

            const newNode = {
                sectionTitle: (
                    <MarginInputsTableCollapsibleField
                        fieldDisplayName={displayName}
                        hideGroup={props.hideGroup[hideGroupKey]}
                        handleCollapse={() => {
                            if (secondaryHideGroupKeys) {
                                const newHideGroup = Object.assign(
                                    {},
                                    ...secondaryHideGroupKeys.map((x) => ({
                                        [x.variableName]: props.hideGroup[x.variableName] === false,
                                    })),
                                );
                                props.setHideGroup({
                                    ...props.hideGroup,
                                    ...newHideGroup,
                                    [hideGroupKey]: showGroupChildren,
                                });

                                return;
                            }

                            props.setHideGroup({
                                ...props.hideGroup,
                                [hideGroupKey]: showGroupChildren,
                            });
                        }}
                    />
                ),
                key: `${displayName}`,
                backgroundColor: showGroupChildren ? levelColors[currentLevel] : levelColors[currentLevel - 1],
                secondaryHideGroupKeys,
                isBorderless: false,
            };

            if (secondaryHideGroupKeys) {
                newNode.isBorderless = true;
            }

            return newNode;
        };

        const columnHeaders = [
            { sectionTitle: '' },
            ...Object.values(columnHeadersRow)
                .flat() // For production lines.
                .filter((x) => x.displayName)
                .map((x) => {
                    const firstLevelChildren =
                        x?.children?.filter((y: DemeterCalculationEngineLineShortModelWithChildren) => !props.hideRows.includes(y.variableName)) ?? [];
                    const finalNodes = [];

                    if (columnHeadersRow.productionLines.length > 0 && x.variableName === 'production') {
                        const informationText = translations.risk.margin.text.productionInformation;
                        return getBaseNode(x.displayName, 1, x.variableName, informationText);
                    }

                    // Childless top level nodes.
                    if (firstLevelChildren.length === 0 && !x.stickyColumnElement) {
                        return getBaseNode(x.displayName, 1, x.variableName, undefined, x.isBorderless);
                    }

                    if (props.hideGroup[x.variableName] === false) {
                        (firstLevelChildren as DemeterCalculationEngineLineShortModelWithChildren[]).forEach((firstLevelChild) => {
                            const firstLevelGrandchildren = firstLevelChild.children?.filter((y) => !props.hideRows.includes(y.variableName));
                            const firstLevelDisplayName = firstLevelChild.displayName;

                            firstLevelGrandchildren?.forEach((secondLevelChildModel) => {
                                const secondLevelGrandChildren = secondLevelChildModel.children;
                                const secondLevelDisplayName = secondLevelChildModel.displayName;

                                if (props.hideGroup[firstLevelChild.variableName] !== false) {
                                    return;
                                }

                                // Childless grandchildren.
                                if (!secondLevelGrandChildren || secondLevelGrandChildren?.length === 0) {
                                    const thirdLevelNode = getBaseNode(secondLevelDisplayName, 3, secondLevelChildModel.variableName);
                                    finalNodes.push(thirdLevelNode);
                                    return;
                                }

                                // Childless great grandchildren.
                                if (secondLevelGrandChildren && props.hideGroup[secondLevelChildModel.variableName] === false) {
                                    secondLevelGrandChildren?.forEach((thirdLevelChild) => {
                                        const childlessFourthLevelNode = getBaseNode(thirdLevelChild.displayName, 3, secondLevelChildModel.variableName);
                                        finalNodes.push(childlessFourthLevelNode);
                                    });
                                }

                                // Grandchildren with children.
                                const thirdLevelNodeWithChildren = getNodeWithChildren(
                                    secondLevelDisplayName,
                                    3,
                                    secondLevelChildModel.variableName,
                                    secondLevelChildModel.combinedWith,
                                );
                                finalNodes.push(thirdLevelNodeWithChildren);
                            });

                            if (firstLevelChild.stickyColumnElement) {
                                finalNodes.push({
                                    sectionTitle: firstLevelChild.stickyColumnElement,
                                    key: firstLevelChild.key,
                                    backgroundColor: levelColors[1],
                                });
                                return;
                            }

                            // Childless children.
                            if (!firstLevelGrandchildren) {
                                const childlessFirstLevelNode = getBaseNode(firstLevelDisplayName, 2, firstLevelChild.variableName);
                                finalNodes.push(childlessFirstLevelNode);
                                return;
                            }

                            // Children with children.
                            const secondLevelNodeWithChildren = getNodeWithChildren(
                                firstLevelDisplayName,
                                2,
                                firstLevelChild.variableName,
                                firstLevelChild.combinedWith,
                            );
                            finalNodes.push(secondLevelNodeWithChildren);
                        });
                    }

                    const firstLevelNodeWithChildren = getNodeWithChildren(x.displayName, 1, x.variableName, x.combinedWith);

                    if (x.stickyColumnElement) {
                        const showGroupChildren = props.hideGroup[x.variableName] === false;

                        finalNodes.push({
                            sectionTitle: x.stickyColumnElement,
                            backgroundColor: showGroupChildren ? levelColors[1] : levelColors[0],
                            isBorderless: false,
                        });

                        return finalNodes;
                    }

                    finalNodes.push(firstLevelNodeWithChildren);

                    return finalNodes;
                }),
        ].flat();

        return (
            <div className={styles.margin_inputs_table_sticky_column}>
                <StickyColumnGroup columnHeaders={columnHeaders} />
            </div>
        );
    }, [props.hideGroup, props.getFarmerMarginCalculatorCompositeRowsModel]);

    return stickyColumns;
};

export default MarginInputsTableStickyColumn;
